/**
 * ToolsPage — Outils de calcul astrologique
 *
 * Grille d'accès aux 3 outils : signature énergétique, aspects, configurations.
 * Affiche aussi les clients avec thème natal importé pour un accès rapide.
 * Design aligné sur le site PHP (astro.arketyp.fr).
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch } from '../utils/api.js';
import { colors } from '../styles/tokens.js';
import type { Client, ClientsResponse } from '../types/clients.js';

const TOOLS = [
  {
    id: 'signature',
    label: 'Signature énergétique',
    description:
      'Calcul de la répartition par éléments (Feu, Terre, Air, Eau), modes (Cardinal, Fixe, Mutable) et polarités. Identifie la dominante.',
    icon: 'bi-bar-chart-line',
    bg: '#FBF6EC',
    fg: colors.accent,
  },
  {
    id: 'aspects',
    label: 'Aspects planétaires',
    description:
      'Détection des aspects entre planètes : conjonctions, oppositions, trigones, carrés, sextiles et quinconces. Calcul des orbes.',
    icon: 'bi-diagram-3',
    bg: '#f0f9ff',
    fg: '#0369a1',
  },
  {
    id: 'configurations',
    label: 'Configurations majeures',
    description:
      'Identification des figures : Yod (Doigt de Dieu), T-Carré, Grand Trigone, Grand Carré, Cerf-Volant. Analyse des planètes impliquées.',
    icon: 'bi-stars',
    bg: '#faf5ff',
    fg: '#7c3aed',
  },
];

export function ToolsPage() {
  const [clients, setClients] = useState<Client[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    apiFetch<ClientsResponse>('/clients?limit=50')
      .then((res) => {
        if (res.success && res.data) {
          setClients(res.data.clients.filter((c) => c.charts.length > 0));
        }
      })
      .finally(() => setIsLoading(false));
  }, []);

  function formatDate(dateStr: string): string {
    try {
      return new Date(dateStr).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
    } catch {
      return dateStr;
    }
  }

  return (
    <div style={{ maxWidth: 1200, width: '100%' }}>
      {/* En-tête */}
      <div style={{ marginBottom: 32 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
          <i className="bi bi-wrench" style={{ fontSize: 20, color: colors.accent }} />
          <h1
            style={{
              margin: 0,
              fontSize: '1.75rem',
              fontWeight: 600,
              color: colors.text,
            }}
          >
            Outils de calcul
          </h1>
        </div>
        <p style={{ margin: 0, fontSize: 14, color: colors.textMid, marginLeft: 30 }}>
          Outils d'analyse astrologique disponibles depuis la fiche d'un client.
        </p>
      </div>

      {/* Grille des outils */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '1.25rem', marginBottom: 32 }}>
        {TOOLS.map((tool) => (
          <div
            key={tool.id}
            style={{
              background: '#fff',
              borderRadius: 10,
              padding: '1.5rem',
              border: `1px solid ${colors.border}`,
              boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: 10,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1.3rem',
                marginBottom: 16,
                background: tool.bg,
                color: tool.fg,
              }}
            >
              <i className={`bi ${tool.icon}`} />
            </div>
            <div style={{ fontSize: '1.05rem', fontWeight: 600, color: colors.text, marginBottom: 8 }}>
              {tool.label}
            </div>
            <div style={{ fontSize: 13, color: colors.textMid, lineHeight: 1.6, flex: 1 }}>
              {tool.description}
            </div>
          </div>
        ))}
      </div>

      {/* Accès rapide — Clients avec thème natal */}
      <div style={{ marginBottom: 32 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <i className="bi bi-people" style={{ fontSize: 18, color: colors.accent }} />
          <h2 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 600, color: colors.text }}>
            Clients avec thème natal
          </h2>
        </div>

        {isLoading ? (
          <div style={{ textAlign: 'center', padding: '32px 0', color: colors.textMuted, fontSize: 14 }}>
            Chargement...
          </div>
        ) : clients.length === 0 ? (
          <div
            style={{
              textAlign: 'center',
              padding: '32px 0',
              background: '#fff',
              borderRadius: 10,
              border: `1px solid ${colors.border}`,
            }}
          >
            <i className="bi bi-people" style={{ fontSize: 32, color: '#ccc', display: 'block', marginBottom: 8 }} />
            <p style={{ fontSize: 14, color: colors.textMuted, margin: '0 0 12px' }}>
              Aucun client avec un thème natal importé.
            </p>
            <Link
              to="/clients"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '8px 16px',
                borderRadius: 6,
                background: colors.accent,
                color: colors.text,
                fontSize: 13,
                fontWeight: 600,
                textDecoration: 'none',
                fontFamily: 'inherit',
              }}
            >
              <i className="bi bi-plus-lg" style={{ fontSize: 12 }} />
              Ajouter un client
            </Link>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {clients.map((client) => (
              <Link
                key={client.id}
                to={`/clients/${client.id}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 14,
                  padding: '14px 20px',
                  background: '#fff',
                  borderRadius: 10,
                  border: `1px solid ${colors.border}`,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                  textDecoration: 'none',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = colors.accent;
                  e.currentTarget.style.boxShadow = `0 2px 8px ${colors.accent}1f`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = colors.border;
                  e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.04)';
                }}
              >
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    background: colors.accentLight,
                    color: colors.accentDark,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 600,
                    fontSize: 15,
                    flexShrink: 0,
                  }}
                >
                  {client.prenom[0]}{client.nom[0]}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '0.95rem', fontWeight: 600, color: colors.text }}>
                    {client.prenom} {client.nom}
                  </div>
                  <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 2 }}>
                    {formatDate(client.dateNaissance)}
                    {client.lieuNaissance && ` — ${client.lieuNaissance}`}
                  </div>
                </div>
                <span
                  style={{
                    fontSize: 12,
                    background: colors.accentLight,
                    color: colors.accentDark,
                    padding: '3px 10px',
                    borderRadius: 12,
                    flexShrink: 0,
                    fontWeight: 500,
                  }}
                >
                  {client.charts.some((c) => c.type === 'NATAL') ? 'TN' : ''}{client.charts.some((c) => c.type === 'NATAL') && client.charts.some((c) => c.type === 'REVOLUTION_SOLAIRE') ? ' + ' : ''}{client.charts.some((c) => c.type === 'REVOLUTION_SOLAIRE') ? 'RS' : ''}
                </span>
                <i className="bi bi-chevron-right" style={{ fontSize: 12, color: '#ccc', flexShrink: 0 }} />
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Note informative */}
      <div
        style={{
          background: colors.accentLight,
          borderRadius: 10,
          padding: '20px 24px',
          borderLeft: `4px solid ${colors.accent}`,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <i className="bi bi-info-circle" style={{ fontSize: 16, color: colors.accentDark, marginTop: 2, flexShrink: 0 }} />
          <div>
            <p style={{ margin: '0 0 6px', fontSize: 14, fontWeight: 600, color: colors.text }}>
              Comment utiliser les outils
            </p>
            <p style={{ margin: 0, fontSize: 13, color: colors.text, lineHeight: 1.6 }}>
              Importez d'abord un fichier CSV Olympia depuis la fiche d'un client.
              Les outils d'analyse (signature, aspects, configurations) seront alors
              disponibles automatiquement sur la fiche du client.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
